import { getDownloadURL, getStorage, ref, uploadBytesResumable } from 'firebase/storage';

const PROFILE_IMAGE_MAX_SIZE = 1200;
const PROFILE_IMAGE_QUALITY = 0.72;
const PROFILE_UPLOAD_TIMEOUT_MS = 50000;

const sanitizeStorageFileName = (value = 'rostro') => {
  const cleanValue = String(value || 'rostro')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);

  return cleanValue || 'rostro';
};

const loadImageFromFile = (file) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('No se pudo leer la imagen.'));
    };

    image.src = objectUrl;
  });

const compressProfileImage = async (file) => {
  if (!file?.type?.startsWith('image/')) {
    throw new Error('La foto debe ser una imagen.');
  }

  if (typeof document === 'undefined') {
    return {
      blob: file,
      name: sanitizeStorageFileName(file.name || 'rostro.jpg'),
      contentType: file.type || 'image/jpeg',
      originalSize: file.size || 0,
      compressedSize: file.size || 0,
      compressionApplied: false,
    };
  }

  const image = await loadImageFromFile(file);
  const scale = Math.min(1, PROFILE_IMAGE_MAX_SIZE / Math.max(image.width, image.height, 1));
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) {
    return {
      blob: file,
      name: sanitizeStorageFileName(file.name || 'rostro.jpg'),
      contentType: file.type || 'image/jpeg',
      originalSize: file.size || 0,
      compressedSize: file.size || 0,
      compressionApplied: false,
    };
  }

  context.drawImage(image, 0, 0, width, height);

  const blob = await new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', PROFILE_IMAGE_QUALITY);
  });

  if (!blob) {
    return {
      blob: file,
      name: sanitizeStorageFileName(file.name || 'rostro.jpg'),
      contentType: file.type || 'image/jpeg',
      originalSize: file.size || 0,
      compressedSize: file.size || 0,
      compressionApplied: false,
    };
  }

  const baseName = sanitizeStorageFileName((file.name || 'rostro').replace(/\.[^.]+$/, ''));
  return {
    blob,
    name: `${baseName}.jpg`,
    contentType: 'image/jpeg',
    originalSize: file.size || 0,
    compressedSize: blob.size || 0,
    compressionApplied: true,
  };
};

const uploadBlob = ({ storageRef, blob, metadata, onProgress }) =>
  new Promise((resolve, reject) => {
    const uploadTask = uploadBytesResumable(storageRef, blob, metadata);
    const timeoutId = setTimeout(() => {
      uploadTask.cancel();
      reject(new Error('La subida de la foto tardó demasiado.'));
    }, PROFILE_UPLOAD_TIMEOUT_MS);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const totalBytes = Number(snapshot.totalBytes) || 0;
        const transferredBytes = Number(snapshot.bytesTransferred) || 0;
        const progress = totalBytes > 0 ? Math.round((transferredBytes / totalBytes) * 100) : 0;
        onProgress?.(Math.min(100, Math.max(0, progress)));
      },
      (error) => {
        clearTimeout(timeoutId);
        reject(error);
      },
      () => {
        clearTimeout(timeoutId);
        resolve(uploadTask.snapshot);
      }
    );
  });

const normalizePathSegment = (value = '', fallback = 'sin-id') => {
  const normalized = String(value || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return normalized || fallback;
};

export const uploadCompressedProfilePhoto = async ({
  file,
  companyId = 'default',
  entityType = 'drivers',
  entityId = '',
  fileNameBase = 'rostro',
  onProgress,
}) => {
  const compressedFile = await compressProfileImage(file);
  const storage = getStorage();
  const safeCompanyId = normalizePathSegment(companyId, 'default');
  const safeEntityType = normalizePathSegment(entityType, 'profiles');
  const safeEntityId = normalizePathSegment(entityId, `draft-${Date.now()}`);
  const safeFileName = sanitizeStorageFileName(compressedFile.name || `${fileNameBase}.jpg`);
  const storagePath = `profiles/${safeEntityType}/${safeCompanyId}/${safeEntityId}/${Date.now()}-${safeFileName}`;
  const storageRef = ref(storage, storagePath);

  const snapshot = await uploadBlob({
    storageRef,
    blob: compressedFile.blob,
    metadata: {
      contentType: compressedFile.contentType || 'image/jpeg',
      customMetadata: {
        companyId: safeCompanyId,
        entityType: safeEntityType,
        entityId: safeEntityId,
      },
    },
    onProgress,
  });

  const downloadURL = await getDownloadURL(snapshot.ref);

  return {
    photoURL: downloadURL,
    storagePath,
    originalSize: compressedFile.originalSize,
    compressedSize: compressedFile.compressedSize,
    compressionApplied: compressedFile.compressionApplied,
  };
};

export const getProfilePhotoUploadErrorMessage = (error) => {
  const code = String(error?.code || '');
  const message = String(error?.message || '');

  if (code.includes('storage/unauthorized')) {
    return 'Storage rechazó la foto. Revisá permisos del proyecto.';
  }

  if (code.includes('storage/canceled') || message.includes('tardó demasiado')) {
    return 'La subida de la foto tardó demasiado. Probá de nuevo con mejor conexión.';
  }

  if (code.includes('storage/bucket-not-found') || code.includes('storage/object-not-found')) {
    return 'No encuentro el bucket de Storage configurado.';
  }

  if (code.includes('storage/retry-limit-exceeded')) {
    return 'Storage no respondió a tiempo. Probá nuevamente.';
  }

  return 'No pude subir la foto. Revisá Storage y volvé a intentar.';
};
